/**
 * The Call button in the top bar (every screen), with its shortcut; "On call mm:ss" during a
 * call (click to focus the panel). And the Home card: "Talk it through · N need you".
 */
import { AudioLines, Phone, ShieldCheck } from 'lucide-react'
import { useMaybeCall } from '../../lib/call/CallProvider.js'
import { guessSpeechPath } from '../../lib/call/local-speech.js'
import { modeLabel } from './CallDock.js'
import { CallTimer } from './CallTimer.js'
import './call.css'

export function CallButton() {
  const c = useMaybeCall()
  if (!c) return null
  const s = c.state
  if (s.phase === 'live' || s.phase === 'starting')
    return (
      <button
        type="button"
        className={`call-oncall${s.view === 'min' ? ' is-min' : ''}`}
        title={s.view === 'min' ? 'Back to the call' : 'On a call'}
        aria-label={`On call${s.view === 'min' ? ': open the call' : ''}`}
        data-testid="call-pill"
        onClick={() => {
          if (s.view === 'min') c.restore()
          else document.querySelector<HTMLElement>('.call-dock')?.focus()
        }}
      >
        <span className="call-oncall__w" aria-hidden="true">
          <AudioLines size={16} />
        </span>
        On call
        {s.startedAt ? <CallTimer since={s.startedAt} /> : null}
      </button>
    )
  return (
    <button
      type="button"
      className="call-btn"
      title={
        c.mac
          ? 'Start a call: talk through what needs you'
          : 'Calls run on your Mac, and none is online'
      }
      aria-keyshortcuts="Meta+Shift+V Control+Shift+V"
      disabled={!c.macStatus}
      onClick={() => void c.start()}
    >
      <Phone size={15} aria-hidden="true" />
      <span>Call</span>
      <span className="call-kcs" aria-hidden="true">
        <kbd>⌘</kbd>
        <kbd>⇧</kbd>
        <kbd>V</kbd>
      </span>
    </button>
  )
}

/** How the next call will likely hear you (no probing on render; the call confirms it). */
export function useSpeechMode() {
  const c = useMaybeCall()
  return guessSpeechPath(c?.mac)
}

export function TalkCard() {
  const c = useMaybeCall()
  const mode = useSpeechMode()
  if (!c || !c.needs || c.state.phase === 'live' || c.state.phase === 'starting') return null
  return (
    <section
      className="call-talk call-breathe"
      aria-label="Talk it through"
      data-testid="talk-card"
    >
      <div className="call-talk__l">
        <span className="call-talk__ic">
          <AudioLines size={24} aria-hidden="true" />
        </span>
        <span>
          <h2>
            Talk it through ·{' '}
            <span className="call-wait num">
              {c.needs} {c.needs === 1 ? 'needs' : 'need'} you
            </span>
          </h2>
          <p className="call-meta">
            Viko reads each one with its options, you answer out loud, and landings wait for one
            passkey at the end.
          </p>
        </span>
      </div>
      <span className="call-talk__r">
        <span className="call-mode">
          <ShieldCheck size={16} aria-hidden="true" />
          {modeLabel(mode?.path, mode?.label)}
        </span>
        <button
          type="button"
          className="call-btn-pri"
          disabled={!c.macStatus}
          title={c.macStatus ? undefined : 'Calls run on your Mac, and none is set up'}
          onClick={() => void c.start()}
        >
          <Phone size={16} aria-hidden="true" />
          Start call
        </button>
      </span>
    </section>
  )
}
